// 클레임 기준(1단계) 화면 공용 — 열거값 라벨 · 원·% 표기 · 오류 문구 · 변경이력 라벨.
// 열거값 라벨의 정본은 서버(services/claim_master.RULE_LABELS)다 — GET /api/claims/standards 의 rule_labels 가 오면 그것을 쓰고,
// 아래 표는 응답에 없을 때의 폴백이다(값은 서버와 같다).

export const FALLBACK_LABELS = {
  accident_type: { general: '일반사고(아워박스 과실)', courier: '택배사고' },
  rule_group: {
    missing: '누락', misship: '오배송', courier_damage: '파손', courier_loss: '분실', courier_delay: '배송지연',
  },
  comp_scope: { select: '범위 선택', all: '전제품 고정' },
  fault: { center: '센터', courier: '택배사', shipper: '고객사·면책' },
  extent: { partial: '일부', all: '전제품', na: '해당없음' },
  temp_zone: { ambient: '상온', cold: '냉장·냉동' },
  handling: { reship: '재출고', refund: '환불', reship_refund: '재출고/환불' },
  comp_mark: { O: '변상', X: '미변상', '△': '조건부(오배송 제품 회수 후 재고복구되면 미변상)' },
  deadline_basis: { pickup_date: '집하일', ship_date: '출고일', expected_delivery: '배송예정일' },
  value_type: { int: '정수', text: '한 줄 글자', longtext: '여러 줄 글자', days_list: '일수 목록', ident: '표 이름' },
  category: { accident: '사고 후보', shipper_customer: '화주·고객 사유', review: '판단 필요' },
};

// 화면 탭 이름(사유코드 관리) — 사고구분 accident_type 값과 같은 키.
export const ACCIDENT_TABS = [
  { key: 'general', label: '일반사고' },
  { key: 'courier', label: '택배사고' },
];
// 사유코드 상세의 사고구분 선택지(구성안⑦ «일반사고 (아워박스 과실)»).
export const ACCIDENT_DETAIL_LABELS = { general: '일반사고 (아워박스 과실)', courier: '택배사고' };
// 범위(일부/전제품)를 고르는 보상기준 묶음 — 일반사고 묶음. 서버 services/claim_master.GENERAL_RULE_GROUPS 와 같다.
export const GENERAL_RULE_GROUPS = ['missing', 'misship'];

/**
 * 보상기준 연결 유형 표기 — 서버 services/claim_master.link_label 과 같은 규칙(범위 낱말은 고정값 일부·전제품).
 * 묶음이 없으면 '건별 판정' · 일반 묶음 + select = '{라벨}(일부) / {라벨}(전제품)' · 일반 묶음 + all = '{라벨}(전제품)' · 그 밖에는 라벨 그대로.
 */
export function linkLabel(item, groupLabel) {
  const group = item ? item.comp_rule_group : null;
  if (!group) return '건별 판정';
  const label = groupLabel || group;
  if (GENERAL_RULE_GROUPS.includes(group)) {
    if (item.comp_scope === 'select') return `${label}(일부) / ${label}(전제품)`;
    if (item.comp_scope === 'all') return `${label}(전제품)`;
  }
  return label;
}
export const TEMP_ZONES = ['ambient', 'cold'];
export const COMP_MARKS = ['O', 'X', '△'];
export const DEADLINE_BASES = ['pickup_date', 'ship_date', 'expected_delivery'];
export const V2_DETAIL_GROUP = 'CLAIM_RESN_DETAIL_CD';

// 택배사 코드 출처 · 동기화 상태(POST courier-codes/sync 의 sources[].status).
export const SOURCE_LABELS = { v2: '2.0', v1: '1.0' };
export const COURIER_SYNC_STATUS = { ok: '반영', unset: '1.0 테이블 미설정(확인 필요)', error: '실패' };

/** 열거값 → 한글. labels = 서버 rule_labels(없으면 폴백) · 모르는 값은 그대로 · 빈 값은 '—'. */
export function labelOf(kind, value, labels) {
  if (value === null || value === undefined || value === '') return '—';
  const table = (labels && labels[kind]) || FALLBACK_LABELS[kind] || {};
  return Object.prototype.hasOwnProperty.call(table, value) ? table[value] : String(value);
}

/** 원 표기 — 1,200원 · 값이 없으면 '—'. */
export function fmtWon(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toLocaleString('ko-KR')}원` : String(value);
}

/** % 표기 — 90% · 소수는 둘째 자리까지 · 값이 없으면 '—'. */
export function fmtPct(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `${Math.round(n * 100) / 100}%`;
}

/** 오류 문구 — 서버 {detail:{message}} · 글자 detail · 없으면 fallback. */
export function errorText(error, fallback = '저장에 실패했습니다') {
  const detail = error && error.response && error.response.data ? error.response.data.detail : null;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message) return detail.message;
  if (typeof detail === 'string' && detail) return detail;
  return fallback;
}

export const statusOf = (error) => (error && error.response ? error.response.status : 0);

// 입력 글자 → 서버 값. 빈칸 = null · 숫자 모양이면 수 · 그 밖은 글자 그대로(서버가 422 로 알려 준다).
export function numOrNull(text) {
  const s = String(text ?? '').trim();
  if (!s) return null;
  return /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : s;
}
export const textOrNull = (text) => {
  const s = String(text ?? '').trim();
  return s || null;
};
export const asText = (value) => (value === null || value === undefined ? '' : String(value));

/** 바뀐 칸만 — next 의 칸 중 base 와 다른 것({} 이면 바뀐 것이 없다). */
export function changedFields(base, next) {
  const out = {};
  Object.keys(next).forEach((key) => {
    const a = base ? base[key] : undefined;
    const b = next[key];
    const same = (a === null || a === undefined) && (b === null || b === undefined) ? true : a === b;
    if (!same) out[key] = b;
  });
  return out;
}

// ── 변경이력 ──
export const ENTITY_LABELS = {
  comp_rule: '변상 기준',
  cost_item: '비용 단가',
  setting: '공통 규칙',
  courier_group: '택배사 그룹',
  courier_code: '택배사 코드',
  reason: '사유코드',
  reason_link: '2.0 연결',
  v2_reason: '2.0 사유코드',
  shipper_standard: '고객사 기준',
  reason_v1_link: '1.0 연결',
  v1_reason: '1.0 상세사유',
  v2_category: '2.0 상세사유 분류',
};
export const ACTION_LABELS = {
  create: '추가', update: '수정', deactivate: '사용 중지', activate: '다시 사용',
  link: '연결', unlink: '연결 해제', sync: '동기화', confirm: '확정',
};
export const FIELD_LABELS = {
  code: '코드', name: '이름', accident_type: '사고구분', comp_rule_group: '보상기준 연결', comp_scope: '범위',
  default_fault: '귀책 기본값', evidence_min: '최소 증빙 장수', evidence_guide: '증빙 설명', note: '메모',
  portal_name: '포털 표시명', portal_guide: '접수 안내 문구', ai_keywords: 'AI 분류 키워드',
  needs_check: '확인 필요', active: '사용 여부', sort_order: '정렬 순서',
  logistics_comp: '물류비', product_comp: '제품가', stock_adjust: '재고조정', shipper_deadline_days: '고객사 접수기한(일)',
  available: '처리 가능', unit_price: '단가', value: '값',
  group_name: '항목', basis: '기준', unit_price_max: '최대 단가',
  comp_pct: '보상률(%)', box_limit_krw: 'BOX당 한도', deadline_basis: '접수기한 기준', deadline_days: '접수기한(일)',
  loss_basis: '분실 기준', loss_start_days: '분실 시작(일)', loss_end_days: '분실 끝(일)', not_applicable: '해당없음',
  match_keywords: '자동추정 키워드', group_code: '확정 그룹', confirmed: '확정',
  reason_code: '사유코드', v2_detail_cd: '2.0 코드',
  fetched: '조회', added: '추가', updated: '갱신', missing: '없어짐', shipper_key: '고객사',
  doc_ref: '기준 공문 번호', temp_zone: '품온', shipper_id: '고객사 id', shipper_name: '고객사 이름', center_key: '센터',
  product_price_basis: '일반사고 제품가 기준', product_sale_ratio_pct: '판매가 협의 비율(%)',
  category: '분류', v1_code: '1.0 코드',
};

export function historyValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (value === true) return '예';
  if (value === false) return '아니오';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
